import { Ref } from "@tblabs/truffle";
import moment from "moment";
import { DateTimeProvider } from "../Services/DateTimeProvider";


export class TimeUtils
{
    static IsHourSet(v: string | number | boolean | Date): boolean
    {
        if (v instanceof Ref)
            v = v.value;

        if (+v === 0)
            return false;

        if (v instanceof Date)
            return v.getHours() !== 0 || v.getMinutes() !== 0;

        return false;
    }

    static HowLongAgo(date: Date | Ref<Date>, noDataText = "(brak daty)"): string
    {
        if (!date)
            return noDataText;

        if (date instanceof Ref)
            date = date.value;

        if (+date === 0)
            return noDataText;

        return moment(date).from(DateTimeProvider.Now);
    }

    static TimeTo(date: Date | Ref<Date>, noDataText = "(brak daty)"): string
    {
        if (!date)
            return noDataText;

        if (date instanceof Ref)
            date = date.value;

        if (+date === 0)
            return noDataText;

        return moment(date).from(DateTimeProvider.Now);
    }

    static DaysTo(date: Date | Ref<Date>, noDataText = "(brak daty)"): string
    {
        if (!date)
            return noDataText;

        if (date instanceof Ref)
            date = date.value;

        if (+date === 0)
            return noDataText;

        return moment(date).diff(DateTimeProvider.Now, "days").toString();
    }

    static TimeLeftTo(date: Date | Ref<Date>, noDataText = "(brak daty)", includeDayName = false): string
    {
        if (!date)
            return noDataText;

        if (date instanceof Ref)
            date = date.value;

        if (+date === 0)
            return noDataText;

        if (moment(date).isSame(DateTimeProvider.Now, "date"))
            return "dzisiaj";
        if (moment(date).isSame(moment(DateTimeProvider.Now).subtract(1, "day"), "date"))
            return "wczoraj";
        if (moment(date).isSame(moment(DateTimeProvider.Now).add(1, "day"), "date"))
            return "jutro";

        const days = moment(date).diff(DateTimeProvider.Now, "days") + 1;

        if (days === (-1))
            return "wczoraj";
        // if (days === 0)
        //     return "dzisiaj";
        if (days >= 7)
            return `za ${days} dni`;
        if (days > 0 && days < 7)
        {
            if (includeDayName)
                return `za ${days} dni, w ${moment(date).format("dddd")}`;
            else
                return `za ${days} dni`;
        }
        if (days < 0)
            return `${-days} dni temu`;

        return moment(date).from(DateTimeProvider.Now);
    }

    static FormattedRange(From: Ref<Date>, To: Ref<Date>): string
    {
        // Funkcja pomocnicza do sformatowania daty w formacie "d.m.rrrr"
        const formatDate = (date: Date) =>
        {
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            return `${day}.${month}.${year}`;
        }

        // Parsowanie dat
        const startDate = new Date(From.value);
        const endDate = new Date(To.value);

        // Formatowanie dat na "d.m.rrrr"
        const formattedStartDate = formatDate(startDate);
        const formattedEndDate = formatDate(endDate);

        if (formattedStartDate === formattedEndDate)
            return formattedStartDate;

        // Sprawdzanie, czy daty są w tym samym roku
        if (startDate.getFullYear() === endDate.getFullYear())
        {
            if (startDate.getMonth() === endDate.getMonth())
            {
                // Ten sam miesiąc i rok
                return `${startDate.getDate()}—${endDate.getDate()}.${formattedStartDate.slice(3)}`;
            }
            else
            {
                // Inny miesiąc, ten sam rok
                return `${formattedStartDate} — ${formattedEndDate}`;
            }
        }
        else
        {
            // Inny rok
            return `${formattedStartDate} — ${formattedEndDate}`;
        }
    }

    public static FormatDuration(secondsLeft: number | Ref<number>): string
    {
        if (!secondsLeft)
            return "";

        let s = 0;

        if (secondsLeft instanceof Ref)
            s = secondsLeft.value;
        else s = secondsLeft;

        if (s < 0)
            s = -s;

        const days = Math.floor(s / 86400);
        const hours = Math.floor((s % 86400) / 3600);
        const minutes = Math.floor((s % 3600) / 60);
        const seconds = s % 60;

        return (days > 0)
            ? `${days} dni, ${hours.toString()}h i ${minutes.toString()}m`
            : `${hours.toString()}h i ${minutes.toString()}m`
    }

    public static Since(date: Date | Ref<Date>): string
    {
        if (date instanceof Ref)
            date = date.value;
        const secondsAgo = moment(date).diff(DateTimeProvider.Now, "seconds");
        return this.FormatDuration(secondsAgo);
    }

    public static FormattedDate(date: Date | Ref<Date>): string
    {
        if (date instanceof Ref)
        {
            if (!date.IsSet)
                return "(data nieustalona)";

            return moment(date.value).format("DD.MM.YYYY");
        }
        else
        {
            if (+date === 0)
                return "(data nieustalona)";

            return moment(date).format("DD.MM.YYYY");
        }
    }

    public static FormattedDateTime(date?: Date | Ref<Date>): string
    {
        if (!date)
            return "(brak daty)";

        if (date instanceof Ref)
        {
            if (!date.IsSet)
                return "(data nieustalona)";

            return moment(date.value).format("DD.MM.YYYY HH:mm");
        }
        else
        {
            if (+date === 0)
                return "(data nieustalona)";

            return moment(date).format("DD.MM.YYYY HH:mm");
        }
    }

    /*
        TUTAJ ZAKŁADAMY ŻE JEŚLI GODZINA TO 00:00 TO PORA DNIA NIE ZOSTAŁA USTALONA
    */
    public static FormattedDayHour(date?: Date | Ref<Date>): string
    {
        if (!date)
            return "(brak daty)";

        if (date instanceof Ref)
        {
            if (!date.IsSet)
                return "(data nieustalona)";

            const day = moment(date.value).format("DD.MM.YYYY");

            if (!this.IsTimeSet(date.value))
                return day + ", godzina nie ustalona";
            else
                return `${day} ${moment(date.value).format("HH:mm")}`;
        }
        else
        {
            if (+date === 0)
                return "(data nieustalona)";

            const day = moment(date).format("dddd, DD.MM.YYYY");

            if (!this.IsTimeSet(date))
                return day + ", godzina nie ustalona";
            else
                return `${day} ${moment(date).format("HH:mm")}`;
        }
    }

    public static IsTimeSet(date: Date | Ref<Date>): boolean
    {
        if (date instanceof Ref)
            date = date.value;

        return moment(date).hour() !== 0 || moment(date).minute() !== 0 || moment(date).milliseconds() !== 0 || moment(date).seconds() !== 0;
    }
}
